"use client";

import AccreditationValueCircle, {
  type AccreditationValueCircleItem,
} from "@/components/molecules/AccreditationValueCircle";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useMemo, useRef } from "react";

interface MetaItem extends AccreditationValueCircleItem {
  meta_priority: number;
  meta_status?: boolean;
}

interface Accreditation3Data {
  id: number;
  page_id?: number;
  status?: boolean | number;
  background_color?: string;
  title_primary_color?: string;
  title_accent_color?: string;
  body_text_color?: string;
  hero_image?: string;
  hero_image_alt?: string;
  decoration_image?: string;
  decoration_image_alt?: string;
  vision_title_part_1?: string;
  vision_title_part_2?: string;
  vision_description?: string;
  mission_title_part_1?: string;
  mission_title_part_2?: string;
  mission_description?: string;
  values_title_part_1?: string;
  values_title_part_2?: string;
  metadata?: MetaItem[];
}

interface Accreditation3Props {
  data: Accreditation3Data | { data: Accreditation3Data };
}

function resolvePayload(
  data: Accreditation3Props["data"]
): Accreditation3Data | undefined {
  if (!data || typeof data !== "object") return undefined;
  const anyData = data as Record<string, unknown>;
  if ("data" in anyData && anyData.data != null && typeof anyData.data === "object") {
    return anyData.data as Accreditation3Data;
  }
  return data as Accreditation3Data;
}

function PinkRingDecoration() {
  return (
    <svg
      className="h-full w-full text-[#FFD4D0]"
      viewBox="0 0 187 187"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="93.5" cy="93.5" r="88" stroke="currentColor" strokeWidth="1" opacity="0.45" />
      <circle cx="93.5" cy="93.5" r="72" stroke="currentColor" strokeWidth="1" opacity="0.35" />
      <circle cx="93.5" cy="93.5" r="56" stroke="currentColor" strokeWidth="1" opacity="0.28" />
      <circle cx="93.5" cy="93.5" r="40" stroke="currentColor" strokeWidth="1" opacity="0.2" />
    </svg>
  );
}

const Accreditation3 = ({ data }: Accreditation3Props) => {
  const ref = useRef<HTMLElement>(null);
  const section = resolvePayload(data);

  const {
    background_color = "#FFFFFF",
    title_primary_color = "#171719",
    title_accent_color = "#E1473F",
    body_text_color = "#373737",
    hero_image = "",
    hero_image_alt = "",
    decoration_image = "",
    decoration_image_alt = "",
    vision_title_part_1 = "",
    vision_title_part_2 = "",
    vision_description = "",
    mission_title_part_1 = "",
    mission_title_part_2 = "",
    mission_description = "",
    values_title_part_1 = "",
    values_title_part_2 = "",
    metadata = [],
  } = section || {};

  const sortedValues = useMemo(() => {
    if (!metadata?.length) return [];
    return [...metadata]
      .filter((item) => item?.meta_status !== false)
      .sort((a, b) => a.meta_priority - b.meta_priority);
  }, [metadata]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "accreditation_section");
  //   }
  // }, []);

  const st = section?.status;
  const active = st === true || st === 1 || String(st) === "1";
  if (!active) {
    return null;
  }

  const showTopBlock =
    hero_image ||
    vision_description ||
    vision_title_part_1 ||
    vision_title_part_2 ||
    mission_description ||
    mission_title_part_1 ||
    mission_title_part_2;

  const showValuesHeadline = values_title_part_1 || values_title_part_2;

  if (!showTopBlock && sortedValues.length === 0) {
    return null;
  }

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "relative flex w-full flex-col items-center overflow-hidden",
        /* Mobile Figma 5024: px 43 py 32; desktop unchanged */
        "px-[43px] py-8 min-[1024px]:px-16 min-[1024px]:py-12 min-[1280px]:px-[104px] min-[1280px]:py-[72px]",
      )}
      style={{
        backgroundColor: background_color,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {/* Desktop: large ring top-left */}
      <div className="pointer-events-none absolute left-4 top-[-28px] hidden h-[187px] w-[187px] min-[1024px]:left-6 min-[1024px]:block">
        {decoration_image ? (
          <Image
            src={decoration_image}
            alt={decoration_image_alt || ""}
            width={187}
            height={187}
            className="h-full w-full object-contain"
          />
        ) : (
          <PinkRingDecoration />
        )}
      </div>

      <div className="relative z-[1] flex w-full max-w-5xl flex-col items-center gap-6 min-[1024px]:gap-[31px]">
        {showTopBlock ? (
          <div
            className={cn(
              "flex w-full flex-col items-center justify-center gap-6 min-[1024px]:gap-8",
              "min-[1024px]:flex-row min-[1024px]:items-center min-[1024px]:gap-[78px]"
            )}
          >
            {hero_image ? (
              <div
                className={cn(
                  "relative hidden h-[280px] w-full max-w-[316px] shrink-0 overflow-hidden",
                  "rounded-tr-[34px] rounded-bl-[34px] shadow-[0_8px_32px_rgba(0,0,0,0.08)]",
                  "min-[1024px]:block min-[1024px]:h-[332px]"
                )}
              >
                <Image
                  src={hero_image}
                  alt={hero_image_alt || "Campus"}
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 316px"
                  priority={false}
                />
              </div>
            ) : null}

            <div
              className={cn(
                "flex w-full max-w-[520px] flex-col gap-6",
                "items-center text-center min-[1024px]:w-[483px] min-[1024px]:shrink-0 min-[1024px]:items-start min-[1024px]:text-left"
              )}
            >
              {(vision_title_part_1 ||
                vision_title_part_2 ||
                vision_description) && (
                <div className="relative w-full max-[1024px]:pt-2">
                  {/* Mobile Figma: small ring behind Vision */}
                  <div
                    className="pointer-events-none absolute left-[-72px] top-[-56px] h-[125px] w-[125px] min-[1024px]:hidden"
                    aria-hidden
                  >
                    {decoration_image ? (
                      <Image
                        src={decoration_image}
                        alt={decoration_image_alt || ""}
                        width={125}
                        height={125}
                        className="h-full w-full object-contain opacity-90"
                      />
                    ) : (
                      <PinkRingDecoration />
                    )}
                  </div>
                  <div className="relative flex w-full flex-col gap-2">
                    {(vision_title_part_1 || vision_title_part_2) && (
                      <p className="text-[28px] font-semibold leading-[1.26] tracking-[-0.02em] min-[1024px]:text-[32px]">
                        {vision_title_part_1 ? (
                          <span style={{ color: title_primary_color }}>
                            {vision_title_part_1}
                          </span>
                        ) : null}
                        {vision_title_part_2 ? (
                          <span style={{ color: title_accent_color }}>
                            {vision_title_part_2}
                          </span>
                        ) : null}
                      </p>
                    )}
                    {vision_description ? (
                      <p
                        className="mx-auto w-full max-w-[261px] text-sm leading-normal min-[1024px]:mx-0 min-[1024px]:max-w-none min-[1024px]:text-[18px]"
                        style={{ color: body_text_color }}
                      >
                        {vision_description}
                      </p>
                    ) : null}
                  </div>
                </div>
              )}

              {(mission_title_part_1 ||
                mission_title_part_2 ||
                mission_description) && (
                <div className="flex w-full flex-col gap-2">
                  {(mission_title_part_1 || mission_title_part_2) && (
                    <p className="text-[28px] font-semibold leading-[1.26] tracking-[-0.02em] min-[1024px]:text-[32px]">
                      {mission_title_part_1 ? (
                        <span style={{ color: title_primary_color }}>
                          {mission_title_part_1}
                        </span>
                      ) : null}
                      {mission_title_part_2 ? (
                        <span style={{ color: title_accent_color }}>
                          {mission_title_part_2}
                        </span>
                      ) : null}
                    </p>
                  )}
                  {mission_description ? (
                    <p
                      className="mx-auto w-full max-w-[297px] text-sm leading-normal min-[1024px]:mx-0 min-[1024px]:max-w-none min-[1024px]:text-[18px]"
                      style={{ color: body_text_color }}
                    >
                      {mission_description}
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        ) : null}

        {sortedValues.length > 0 ? (
          <>
            <div
              className="hidden h-0.5 w-full max-w-5xl shrink-0 rounded-full min-[1024px]:block"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, rgb(211, 211, 211) 13%, rgb(109, 109, 109) 50%, rgb(227, 227, 227) 77%)",
              }}
              aria-hidden
            />

            <div className="relative flex w-full max-w-5xl flex-col items-center gap-6 min-[1024px]:gap-[25px]">
              {showValuesHeadline ? (
                <h2 className="text-center text-[28px] font-semibold leading-[1.26] tracking-[-0.02em] min-[1024px]:text-[32px]">
                  {values_title_part_1 ? (
                    <span style={{ color: title_primary_color }}>
                      {values_title_part_1}
                    </span>
                  ) : null}
                  {values_title_part_2 ? (
                    <span style={{ color: title_accent_color }}>
                      {values_title_part_2}
                    </span>
                  ) : null}
                </h2>
              ) : null}

              <div className="flex w-full items-center justify-center min-[1024px]:ml-6">
                {/* No mobile gap: circles overlap via each card's -mt-3 (vertical
                    stack). gap-4 only applies on desktop, where it pairs with the
                    cards' -ml-6 for the horizontal-row overlap. */}
                <div className="flex flex-col justify-center min-[1024px]:flex-row min-[1024px]:gap-4">
                  {sortedValues.map((item, index) => (
                    <AccreditationValueCircle
                      key={`${item.id}-${index}`}
                      item={item}
                      stackIndex={index}
                    />
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
};

export default Accreditation3;
